import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const TourPrograms = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTour, setEditTour] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Village autocomplete
  const [villList, setVillList] = useState([]);
  const [villSearch, setVillSearch] = useState("");
  const [villDropdown, setVillDropdown] = useState([]);
  const [showVillDrop, setShowVillDrop] = useState(false);
  const villRef = useRef(null);

  // Document upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [filePreview, setFilePreview] = useState(null);

  const MAX_FILE_SIZE_MB = 2; // 2MB max
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
  ];

  const emptyForm = {
    tourname: "",
    villageid: "",
    tourdate: "",
    reference: "",
    isattending: false,
    assignee: "",
  };
  const [form, setForm] = useState(emptyForm);

  // ── Fetch Villages ────────────────────────────────────────
  const fetchVillages = useCallback(async () => {
    try {
      const res = await API.get("/masters/villages");
      setVillList(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Tours ───────────────────────────────────────────
  const fetchTours = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/tourprograms/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
        });
        setTours(res.data.data.tours);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, dateFrom, dateTo],
  );

  useEffect(() => {
    fetchVillages();
    fetchTours();
  }, []);

  // ── Close village dropdown on outside click ───────────────
  useEffect(() => {
    const handler = (e) => {
      if (villRef.current && !villRef.current.contains(e.target)) {
        setShowVillDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Village Autocomplete ──────────────────────────────────
  const handleVillSearch = (val) => {
    setVillSearch(val);
    setForm((prev) => ({ ...prev, villageid: "" }));
    if (val.length >= 1) {
      const filtered = villList.filter((v) =>
        v.name.toLowerCase().includes(val.toLowerCase()),
      );
      setVillDropdown(filtered.slice(0, 8));
      setShowVillDrop(true);
    } else {
      setVillDropdown([]);
      setShowVillDrop(false);
    }
  };

  const selectVillage = (village) => {
    setVillSearch(village.name);
    setForm((prev) => ({ ...prev, villageid: village.villageid }));
    setShowVillDrop(false);
    setVillDropdown([]);
  };

  // ── File Validation & Compression ────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError("");
    setSelectedFile(null);
    setFilePreview(null);

    if (!file) return;

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError("Only JPG, PNG and PDF files are allowed.");
      return;
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      setFileError(
        `File size ${sizeMB.toFixed(1)}MB exceeds ${MAX_FILE_SIZE_MB}MB limit.`,
      );
      return;
    }

    // If image → compress using canvas
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Resize if too large
          const MAX_DIM = 1200;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round((height * MAX_DIM) / width);
              width = MAX_DIM;
            } else {
              width = Math.round((width * MAX_DIM) / height);
              height = MAX_DIM;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
              });
              const compressedMB = compressedFile.size / (1024 * 1024);
              setSelectedFile(compressedFile);
              setFilePreview(canvas.toDataURL("image/jpeg", 0.8));
              console.log(
                `Compressed: ${sizeMB.toFixed(2)}MB → ${compressedMB.toFixed(2)}MB`,
              );
            },
            "image/jpeg",
            0.8,
          );
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      // PDF — no compression needed
      setSelectedFile(file);
      setFilePreview(null);
    }
  };

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditTour(null);
    setForm(emptyForm);
    setVillSearch("");
    setSelectedFile(null);
    setFilePreview(null);
    setFileError("");
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (tour) => {
    setEditTour(tour);
    // Find village name for autocomplete
    const vill = villList.find((v) => v.villageid === tour.villageid);
    setVillSearch(vill ? vill.name : "");
    setSelectedFile(null);
    setFilePreview(null);
    setFileError("");
    setForm({
      tourname: tour.tourname || "",
      villageid: tour.villageid || "",
      tourdate: tour.tourdate
        ? tour.tourdate.replace(" ", "T").slice(0, 16)
        : "",
      reference: tour.reference || "",
      isattending: tour.isattending || false,
      assignee: tour.assignee || "",
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (fileError) return;
    setSaving(true);
    try {
      let documentid = null;

      // Upload document first if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("reference", form.reference || "TOUR");
        try {
          const docRes = await API.post("/documents/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          documentid = docRes.data.data?.docid || null;
        } catch (docErr) {
          console.warn("Document upload failed:", docErr);
        }
      }

      const payload = {
        tourname: form.tourname,
        villageid: form.villageid ? parseInt(form.villageid) : null,
        tourdate: form.tourdate ? form.tourdate.replace("T", " ") : null,
        reference: form.reference,
        isattending: form.isattending,
        assignee: form.assignee ? parseInt(form.assignee) : null,
        documentid: documentid,
      };

      if (editTour) {
        await API.put(`/tourprograms/${editTour.tourid}`, payload);
      } else {
        await API.post("/tourprograms/", payload);
      }
      setShowModal(false);
      fetchTours(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving tour");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (tourid) => {
    if (!window.confirm("Are you sure you want to delete this tour?")) return;
    setDeleting(tourid);
    try {
      await API.delete(`/tourprograms/${tourid}`);
      fetchTours(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error deleting tour");
    } finally {
      setDeleting(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTours(1);
  };

  const handleReset = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setTimeout(() => fetchTours(1), 100);
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Tour Programs</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> tour programs found
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-plus-lg me-2"></i>Add Tour
        </button>
      </div>

      {/* Search Bar */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <form onSubmit={handleSearch}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label small text-muted mb-1">
                  Tour Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by tour name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small text-muted mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small text-muted mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-1">
                <button type="submit" className="btn btn-primary w-100 mt-3">
                  <i className="bi bi-search"></i>
                </button>
              </div>
              <div className="col-6 col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 mt-3"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-map fs-1 d-block mb-2"></i>
              No tour programs found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Tour Name
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Date</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Reference
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Attending
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Doc</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Created By
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tours.map((tour, idx) => (
                    <tr key={tour.tourid}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <div className="fw-semibold small">
                          {tour.tourname || "—"}
                        </div>
                      </td>
                      <td className="small text-muted">
                        {tour.tourdate || "—"}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border small">
                          {tour.reference}
                        </span>
                      </td>
                      <td>
                        {tour.isattending ? (
                          <span className="badge bg-success-subtle text-success">
                            Yes
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary">
                            No
                          </span>
                        )}
                      </td>
                      <td>
                        {tour.documentid ? (
                          <span className="badge bg-info-subtle text-info">
                            <i className="bi bi-paperclip me-1"></i>Yes
                          </span>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {tour.created_by || "—"}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => openEdit(tour)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(tour.tourid)}
                          disabled={deleting === tour.tourid}
                        >
                          {deleting === tour.tourid ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchTours(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchTours(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchTours(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <h5 className="modal-title fw-bold">
                  {editTour ? "Edit Tour Program" : "Add Tour Program"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                >
                  {/* Tour Name */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Tour Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter tour name"
                      value={form.tourname}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^[a-zA-Z\s]*$/.test(val)) {
                          setForm({ ...form, tourname: val });
                        }
                      }}
                      required
                    />
                    <div className="form-text text-muted">
                      Only alphabets allowed
                    </div>
                  </div>

                  {/* Tour Date */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Tour Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={form.tourdate}
                      onChange={(e) =>
                        setForm({ ...form, tourdate: e.target.value })
                      }
                    />
                  </div>

                  {/* Reference */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Reference <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. REF-001"
                      value={form.reference}
                      onChange={(e) =>
                        setForm({ ...form, reference: e.target.value })
                      }
                      required
                    />
                  </div>

                  {/* Village Autocomplete */}
                  <div className="mb-3" ref={villRef}>
                    <label className="form-label small fw-medium">
                      Village
                    </label>
                    <div className="position-relative">
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type village name to search..."
                          value={villSearch}
                          onChange={(e) => handleVillSearch(e.target.value)}
                          onFocus={() => {
                            if (villDropdown.length > 0) setShowVillDrop(true);
                          }}
                          autoComplete="off"
                        />
                        {form.villageid && (
                          <span className="input-group-text bg-light">
                            <i className="bi bi-check-circle-fill text-success"></i>
                          </span>
                        )}
                      </div>

                      {/* Village Dropdown */}
                      {showVillDrop && villDropdown.length > 0 && (
                        <div
                          className="border rounded-3 shadow-sm bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "180px",
                            overflowY: "auto",
                          }}
                        >
                          {villDropdown.map((v) => (
                            <div
                              key={v.villageid}
                              className="px-3 py-2 border-bottom"
                              style={{ cursor: "pointer" }}
                              onMouseDown={() => selectVillage(v)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div className="small fw-medium">{v.name}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {villSearch &&
                        villDropdown.length === 0 &&
                        showVillDrop && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              No village found
                            </span>
                          </div>
                        )}
                    </div>
                    <div className="form-text text-muted">
                      Start typing village name — select from list
                    </div>
                  </div>

                  {/* Assignee */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Assignee (User ID)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="User ID (optional)"
                      value={form.assignee}
                      onChange={(e) =>
                        setForm({ ...form, assignee: e.target.value })
                      }
                    />
                  </div>

                  {/* Attending */}
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isattending"
                      checked={form.isattending}
                      onChange={(e) =>
                        setForm({ ...form, isattending: e.target.checked })
                      }
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="isattending"
                    >
                      MLA is Attending
                    </label>
                  </div>

                  {/* Document Upload */}
                  <div className="mb-3 p-3 bg-light rounded-3">
                    <label className="form-label small fw-medium mb-2">
                      <i className="bi bi-paperclip me-1"></i>
                      Attach Document
                      <span
                        className="text-muted ms-2"
                        style={{ fontSize: "11px" }}
                      >
                        (JPG, PNG, PDF — max {MAX_FILE_SIZE_MB}MB)
                      </span>
                    </label>

                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                    />

                    {/* File Error */}
                    {fileError && (
                      <div className="alert alert-danger py-2 small mt-2 mb-0">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {fileError}
                      </div>
                    )}

                    {/* Image Preview */}
                    {filePreview && (
                      <div className="mt-2">
                        <div className="small text-muted mb-1">Preview:</div>
                        <img
                          src={filePreview}
                          alt="preview"
                          className="rounded-3 border"
                          style={{
                            maxHeight: "120px",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <div className="text-success small mt-1">
                          <i className="bi bi-check-circle me-1"></i>
                          Image compressed and ready to upload
                        </div>
                      </div>
                    )}

                    {/* PDF Selected */}
                    {selectedFile && !filePreview && (
                      <div className="mt-2 text-success small">
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        {selectedFile.name} (
                        {(selectedFile.size / 1024).toFixed(0)} KB) ready to
                        upload
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving || !!fileError}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editTour ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourPrograms;
